import { createContext, isValidElement, useContext, useMemo, type ComponentProps, type ComponentType, type ReactNode } from "react";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CITATION_HREF, citationLinks, citationMarkers } from "../lib/citations";
import { cn } from "../lib/cn";
import { hidePartial, isolateWidgetMarkers, type InlineWidgetRef } from "../lib/inlineWidgets";
import { remarkInlineWidgets, WIDGET_ELEMENT } from "../lib/remarkInlineWidgets";
import { CitationMarker, type CitationLike } from "./citations";
import { StreamingCaret } from "./motion";
import { arabicMarkdown, useContentLanguage } from "../lib/contentLanguage";
import { useDocumentLocale } from "../lib/microcopy";

/** Element overrides accepted by `MarkdownBody` (re-exported so apps don't depend on react-markdown directly). */
export type MarkdownComponents = Components;

function plainText(n: ReactNode): string {
  if (typeof n === "string" || typeof n === "number") return String(n);
  if (Array.isArray(n)) return n.map(plainText).join("");
  if (isValidElement(n)) return plainText((n.props as { children?: ReactNode }).children);
  return "";
}

/** A quotation carries the language it was written in (WCAG 3.1.2), e.g. an enquirer's English question inside an Arabic answer. */
const QuoteInItsLanguage: NonNullable<Components["blockquote"]> = ({ node: _node, children, ...rest }) => {
  const text = plainText(children);
  const english = /[A-Za-z]/.test(text) && !/[؀-ۿ]/.test(text);
  return <blockquote {...rest} {...(english ? { lang: "en", dir: "ltr" } : { dir: "auto" })}>{children}</blockquote>;
};

type LinkComponent = ComponentType<ComponentProps<"a"> & { node?: unknown }>;

/**
 * What the elements of one MarkdownBody read while they render: its citations, how a source opens, how a placed widget is drawn, whether
 * the text is still streaming and the caller's own link. The elements are module-level components that read it, so the `components` given
 * to react-markdown keep their identity from token to token and nothing (a citation's hover card, a widget's chart) remounts as the text grows.
 */
interface MarkdownCtxValue {
  citations?: CitationLike[];
  onOpenCitation?: (c: CitationLike) => void;
  renderWidget?: (ref: InlineWidgetRef) => ReactNode;
  streaming: boolean;
  Link?: LinkComponent;
}

const MarkdownCtx = createContext<MarkdownCtxValue>({ streaming: false });

const Anchor: NonNullable<Components["a"]> = (props) => {
  const { citations, onOpenCitation, Link } = useContext(MarkdownCtx);
  const href = props.href ?? "";
  if (citations && citations.length > 0 && href.startsWith(CITATION_HREF)) {
    const n = Number(href.slice(CITATION_HREF.length));
    return <CitationMarker n={n} citation={citations.find((c) => c.index === n) ?? citations[n - 1]} onOpen={onOpenCitation} />;
  }
  if (Link) return <Link {...props} />;
  const { node: _node, ...rest } = props;
  return <a {...rest} />;
};

/** A placed widget: the host draws it (a block element between two paragraphs); without a host, nothing. */
function WidgetSlot(props: { wid?: unknown; node?: unknown }) {
  const { renderWidget, streaming } = useContext(MarkdownCtx);
  const id = typeof props.wid === "string" ? props.wid : "";
  if (!id || !renderWidget) return null;
  const drawn = renderWidget({ id, streaming });
  if (drawn === null || drawn === undefined || drawn === false) return null;
  return <div className="md-w not-prose" data-wid={id}>{drawn}</div>;
}

const REMARK = [remarkGfm, remarkInlineWidgets];

/**
 * Renders GFM markdown in the platform's prose style. `components` lets callers override elements (e.g. turn links into footnote chips).
 * Given the `citations`, each in-text source marker previews its source on hover and opens it with `onOpenCitation`. A widget marker
 * (`[[w:<id>]]` alone on its line) is drawn by `renderWidget` where the text places it; one written inside a sentence, a list, a table or
 * under a heading is drawn after it, as the server places it (`normaliseWidgetMarkers`); without `renderWidget` none is drawn at all.
 * While `streaming`, a marker or citation still arriving at the end of the text is held back until it is complete.
 */
export function MarkdownBody({ children, className, streaming, components, citations, onOpenCitation, renderWidget }: {
  children: string; className?: string; streaming?: boolean; components?: Components; citations?: CitationLike[]; onOpenCitation?: (c: CitationLike) => void;
  /** Draws the widget a marker places (ECONIC answers); null draws nothing. */
  renderWidget?: (ref: InlineWidgetRef) => ReactNode;
}) {
  const cited = !!citations && citations.length > 0;
  const Link = components?.a as LinkComponent | undefined;
  const elements = useMemo<Components>(
    () => ({ blockquote: QuoteInItsLanguage, ...components, a: Anchor, [WIDGET_ELEMENT]: WidgetSlot } as Components),
    [components],
  );
  const ctx = useMemo<MarkdownCtxValue>(() => ({ citations, onOpenCitation, renderWidget, streaming: !!streaming, Link }), [citations, onOpenCitation, renderWidget, streaming, Link]);
  const lang = useContentLanguage();
  const ar = useDocumentLocale() === "ar";
  // On an Arabic screen: no demonstration tags, abbreviations as Arabic terms, English passages in their machine translation once known.
  const source = ar && !streaming ? arabicMarkdown(children, lang) : children;
  const text = isolateWidgetMarkers(streaming ? hidePartial(source) : source);
  return (
    <div dir="auto" className={cn("prose prose-stone max-w-none text-sm leading-relaxed [&_h1]:font-heading [&_h2]:font-heading [&_h3]:font-heading [&_h2]:text-lg [&_h3]:text-base [&_table]:text-xs [&_a]:text-aegold-800 [&_blockquote]:border-aegold-400 [&_code]:rounded [&_code]:bg-sand-100 [&_code]:px-1 [&_code]:text-[12px] [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_p]:my-2 [&_li]:my-0.5 [&_hr]:border-sand-200 [&_th]:text-start [&_th]:border-b [&_th]:border-sand-300 [&_td]:border-b [&_td]:border-sand-200 [&_td]:py-1 [&_th]:py-1 [&_td]:pe-3 [&_th]:pe-3 [&_td]:align-top [&_strong]:font-semibold", className)}>
      <MarkdownCtx.Provider value={ctx}>
        <Markdown remarkPlugins={REMARK} components={elements}>{cited ? citationLinks(text) : citationMarkers(text)}</Markdown>
      </MarkdownCtx.Provider>
      <StreamingCaret active={!!streaming} />
    </div>
  );
}
